import { Component } from '@theme/component';
import { isMobileBreakpoint, mediaQueryLarge, prefersReducedMotion } from '@theme/utilities';

const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';
const DEFAULT_DURATION = 700;
const DRAG_THRESHOLD = 8;
const SWIPE_RATIO = 0.15;
const MOBILE_SWIPE = 40;
const INTERACTIVE = 'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"]), slideshow-controls';

class EmeSlideFade extends Component {
  #slideshow = null;
  #gesture = null;
  #suppressClick = false;
  #mediaController = null;
  #mobile = null;
  #resumeOnLarge = false;

  connectedCallback() {
    super.connectedCallback();
    this.#slideshow = this.closest('slideshow-component');
    if (!this.#slideshow) return;

    this.#slideshow.addEventListener('slideshow:select', this.#handleSelect);
    this.#slideshow.addEventListener('mousedown', this.#handleMouseDown, { capture: true });
    this.#slideshow.addEventListener('pointerdown', this.#handlePointerDown);
    this.#slideshow.addEventListener('click', this.#handleClickCapture, { capture: true });
    this.#slideshow.addEventListener('click', this.#handleClick);
    this.#slideshow.addEventListener('focusin', this.#handleFocusIn);
    this.#slideshow.addEventListener('focusout', this.#handleFocusOut);
    this.#holdAutoplay();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#slideshow?.removeEventListener('slideshow:select', this.#handleSelect);
    this.#slideshow?.removeEventListener('mousedown', this.#handleMouseDown, { capture: true });
    this.#slideshow?.removeEventListener('pointerdown', this.#handlePointerDown);
    this.#slideshow?.removeEventListener('click', this.#handleClickCapture, { capture: true });
    this.#slideshow?.removeEventListener('click', this.#handleClick);
    this.#gesture?.controller.abort();
    this.#slideshow?.removeEventListener('focusin', this.#handleFocusIn);
    this.#slideshow?.removeEventListener('focusout', this.#handleFocusOut);
    this.#mediaController?.abort();
  }

  async #holdAutoplay() {
    await customElements.whenDefined('slideshow-component');
    if (!this.isConnected) return;
    this.#mobile = null;
    this.#applyAutoplay();
    this.#mediaController?.abort();
    this.#mediaController = new AbortController();
    mediaQueryLarge.addEventListener('change', () => this.#applyAutoplay(), { signal: this.#mediaController.signal });
  }

  #applyAutoplay() {
    const slideshow = this.#slideshow;
    const mobile = isMobileBreakpoint();
    if (!slideshow?.autoplay || mobile === this.#mobile) return;
    this.#mobile = mobile;
    if (prefersReducedMotion()) {
      slideshow.pause();
      return;
    }
    if (mobile) {
      this.#resumeOnLarge = !slideshow.paused;
      slideshow.pause();
    } else if (this.#resumeOnLarge) {
      slideshow.play();
    }
  }

  get #nativeSlide() {
    return Boolean(this.#slideshow?.hasAttribute('data-eme-mobile-slide')) && isMobileBreakpoint();
  }

  #inScroller(target) {
    return target instanceof Element && Boolean(target.closest('slideshow-slides')) && !target.closest('slideshow-controls');
  }

  #handleMouseDown = (event) => {
    if (this.#nativeSlide || !this.#inScroller(event.target)) return;
    event.stopPropagation();
    event.preventDefault();
  };

  #handlePointerDown = (event) => {
    if (this.#nativeSlide) return;
    this.#gesture?.controller.abort();
    this.#gesture = null;
    this.#suppressClick = false;
    if (!event.isPrimary || event.button !== 0 || !this.#inScroller(event.target)) return;

    const controller = new AbortController();
    const { signal } = controller;
    const width = event.target.closest('slideshow-slides')?.clientWidth ?? 0;
    this.#gesture = { controller, x: event.clientX, y: event.clientY, distance: 0, done: false, threshold: isMobileBreakpoint() ? MOBILE_SWIPE : width * SWIPE_RATIO };
    document.addEventListener('pointermove', this.#handlePointerMove, { signal });
    document.addEventListener('pointerup', this.#endGesture, { signal });
    document.addEventListener('pointercancel', this.#endGesture, { signal });
  };

  #handlePointerMove = (event) => {
    const gesture = this.#gesture;
    if (!gesture || !event.isPrimary) return;
    const dx = event.clientX - gesture.x;
    const dy = event.clientY - gesture.y;
    gesture.distance = Math.max(gesture.distance, Math.hypot(dx, dy));
    if (gesture.distance > DRAG_THRESHOLD) this.#suppressClick = true;
    if (gesture.done || Math.abs(dx) < gesture.threshold || Math.abs(dx) < Math.abs(dy)) return;

    gesture.done = true;
    if (dx < 0) this.#slideshow?.next();
    else this.#slideshow?.previous();
  };

  #endGesture = () => {
    this.#gesture?.controller.abort();
    this.#gesture = null;
  };

  #handleClickCapture = (event) => {
    if (!this.#suppressClick) return;
    this.#suppressClick = false;
    event.preventDefault();
    event.stopPropagation();
  };

  #handleClick = (event) => {
    if (this.#nativeSlide) return;
    if (!(event.target instanceof Element)) return;
    const interactive = event.target.closest(INTERACTIVE);
    if (interactive && this.#slideshow?.contains(interactive)) return;
    this.#slideshow?.next();
  };

  #handleFocusIn = (event) => {
    if (!(event.target instanceof Element) || !event.target.matches(':focus-visible')) return;
    this.#slideshow?.suspend();
  };

  #handleFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.#slideshow?.contains(event.relatedTarget)) return;
    this.#slideshow?.resume();
  };

  #handleSelect = async (event) => {
    if (this.#nativeSlide) return;
    const { index, previousIndex, trigger } = event.detail ?? {};
    if (prefersReducedMotion() || trigger !== 'select' || index === previousIndex) return;

    const outgoing = this.#slideshow?.slides?.[previousIndex];
    if (!outgoing) return;

    const layer = outgoing.cloneNode(true);
    for (const element of [layer, ...layer.querySelectorAll('[id], [ref]')]) {
      element.removeAttribute('id');
      element.removeAttribute('ref');
    }
    layer.removeAttribute('on:click');
    layer.inert = true;
    layer.setAttribute('aria-hidden', 'true');
    layer.classList.add('eme-slide-fade__layer');
    this.replaceChildren(layer);

    const animation = layer.animate([{ opacity: 1 }, { opacity: 0 }], { duration: Number(this.dataset.duration) || DEFAULT_DURATION, easing: EASING, fill: 'forwards' });

    try {
      await animation.finished;
    } catch {
      return;
    }
    layer.remove();
  };
}

if (!customElements.get('eme-slide-fade')) customElements.define('eme-slide-fade', EmeSlideFade);
